import { useCallback, useMemo, useState } from 'react';

import { QueryKey, useQuery, UseQueryOptions } from '@tanstack/react-query';

export type PaginationParams<T> = T & {
  page: number;
  size: number;
};

export function usePagination<TPageParams>(defaultPageParams: PaginationParams<TPageParams>) {
  const [currentPageParams, setCurrentPageParams] = useState(defaultPageParams);

  const fetchPage = useCallback((pageParams: Partial<PaginationParams<TPageParams>>) => {
    setCurrentPageParams((prevParams) => ({
      ...prevParams,
      ...pageParams,
    }));
  }, []);

  const pageContexts = useMemo(
    () => ({
      page: currentPageParams.page,
      size: currentPageParams.size,
      pageParams: currentPageParams,
      fetchPage,
    }),
    [currentPageParams, fetchPage],
  );

  return {
    currentPageParams,
    pageContexts,
  };
}

export function usePaginatedQuery<
  TPageParams,
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
>({
  queryKey,
  queryFn,
  defaultPageParams,
  options,
}: {
  queryKey: QueryKey;
  queryFn: (pageParams: PaginationParams<TPageParams>) => TQueryFnData | Promise<TQueryFnData>;
  defaultPageParams: PaginationParams<TPageParams>;
  options?: UseQueryOptions<TQueryFnData, TError, TData, QueryKey>;
}) {
  const { currentPageParams, pageContexts } = usePagination<TPageParams>(defaultPageParams);
  const { refetch, ...queryResult } = useQuery({
    queryKey: [...queryKey, 'list', currentPageParams],
    queryFn: () => queryFn(currentPageParams),
    keepPreviousData: true,
    ...options,
  });

  const fetchPage = useCallback(
    (pageParams: Partial<PaginationParams<TPageParams>>, force = false) => {
      pageContexts.fetchPage(pageParams);
      if (force) {
        refetch();
      }
    },
    [pageContexts, refetch],
  );

  return {
    ...queryResult,
    ...pageContexts,
    refetch,
    fetchPage,
  };
}
